import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SafeResourceUrl } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

/**
 * Shared invoice PDF viewer, used by both the Appointments (Calendar) module
 * and the Payment (Subscription) module.
 *
 * WHY THIS EXISTS (please read before "reusing the Documents viewer" again):
 * The Documents module's PDF preview lives entirely inside
 * DocumentsComponent's own template/state (`previewDocument`, `isPreviewOpen`,
 * `isPreviewLoading`, the `.preview-modal` markup in documents.component.html).
 * It is not exported as a standalone component, directive, or service, so it
 * cannot be injected into the Appointments or Payment modules without editing
 * the Documents module itself — which is explicitly out of scope for this
 * change.
 *
 * To give the invoice the SAME viewing experience (iframe-based PDF render,
 * same loading spinner behavior, same close behavior) without touching any
 * Documents file, this component reproduces that exact markup/CSS-class
 * structure (`.preview-modal`, `.preview-content`, `.preview-header`,
 * `.iframe-wrapper`, `.iframe-spinner-overlay`, `.preview-body`,
 * `.no-preview`) as a small, input/output-driven, reusable component.
 *
 * WHY STANDALONE:
 * CalendarComponent and SubscriptionComponent live in different NgModules
 * (CalendarComponent is declared in AppModule; SubscriptionComponent is
 * declared in the lazy-loaded SettingsModule). An NgModule declaration is
 * only visible inside that same module, so declaring this component in just
 * one of them left the other module's template unable to resolve
 * `<app-invoice-pdf-viewer>` ("is not a known element" / "Can't bind to
 * 'previewUrl'..."). Making it `standalone: true` lets ANY module or
 * standalone component simply add it to its own `imports: []` array —
 * no shared module needed, and no risk of Angular's "component X is part of
 * two modules" error.
 *
 * If the Documents viewer is ever refactored into a shared component/service,
 * this component should be retired in favor of that one.
 */
@Component({
  selector: 'app-invoice-pdf-viewer',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './invoice-pdf-viewer.component.html',
  styleUrls: ['./invoice-pdf-viewer.component.css'],
})
export class InvoicePdfViewerComponent {
  /** Whether the modal is visible. */
  @Input() isOpen = false;
  /** Whether the invoice PDF is still loading. */
  @Input() isLoading = false;
  /** Sanitized URL (blob: or https:) for the invoice PDF, for use in an <iframe>. */
  @Input() previewUrl: SafeResourceUrl | null = null;
  /** Header title, e.g. "Invoice #12345". */
  @Input() title = 'Invoice';
  /** Emitted when the user closes the viewer. */
  @Output() closed = new EventEmitter<void>();

  close(): void {
    this.closed.emit();
  }
}